import React, { useState, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { validateEmail, validatePhone } from "../../utils";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import {
  Input,
  ErrorText,
  Counter,
  InputWrapper,
  FloatingLabel,
  IconWrapper,
  SuccessIcon,
  ErrorIcon
} from "./styles";

interface KLTextInputProps {
  value: string | null | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  type?: string;
  required?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  disabled?: boolean;
  maxNumOfCharacters?: number;
  validationFn?: (value: string) => { valid: boolean; error?: string };
}

const KLTextInput: React.FC<KLTextInputProps> = ({
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  onValidationChange,
  disabled,
  maxNumOfCharacters,
  validationFn,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const reducedMotion = useReducedMotion();

  // Generate unique IDs for ARIA associations
  const inputId = useId();
  const errorId = `${inputId}-error`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (maxNumOfCharacters && inputValue.length > maxNumOfCharacters) {
      return;
    }

    // Handle type-specific input filtering BEFORE onChange
    if (type === "tel") {
      // Only allow digits
      if (!/^\d*$/.test(inputValue)) {
        return; // Don't process non-digit characters
      }
    }

    onChange(e);

    // Handle empty input first
    if (inputValue === "") {
      setError(null);
      setIsValid(false);
      // Empty field is only valid if not required
      onValidationChange?.(!required);
      return;
    }

    // Handle custom validation function
    if (validationFn) {
      const result = validationFn(inputValue);
      setError(result.error || null);
      setIsValid(result.valid);
      onValidationChange?.(result.valid);
      return;
    }

    // Handle type-specific validation (value is guaranteed non-empty here)
    if (type === "tel") {
      if (validatePhone(inputValue)) {
        setError(null);
        setIsValid(true);
        onValidationChange?.(true);
      } else {
        setError("טלפון בפורמט 05XXXXXXXX");
        setIsValid(false);
        onValidationChange?.(false);
      }
    } else if (type === "email") {
      if (validateEmail(inputValue)) {
        setError(null);
        setIsValid(true);
        onValidationChange?.(true);
      } else {
        setError("someone@example.com");
        setIsValid(false);
        onValidationChange?.(false);
      }
    } else {
      // For generic text inputs - only show success if required
      setError(null);
      setIsValid(!!required);
      onValidationChange?.(true);
    }
  };

  const safeValue = value ?? "";
  const isMaxReached = maxNumOfCharacters && safeValue.length >= maxNumOfCharacters;
  const hasValue = safeValue.length > 0;

  return (
    <>
      <InputWrapper>
        <FloatingLabel
          htmlFor={inputId}
          $hasValue={hasValue}
          $focused={isFocused}
          $error={!!error}
          $disabled={disabled}
        >
          {placeholder}
        </FloatingLabel>
        <Input
          id={inputId}
          type={type}
          inputMode={type === "tel" ? "numeric" : type === "email" ? "email" : undefined}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          value={safeValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          required={required}
          disabled={disabled}
          $hasValue={hasValue}
          $error={!!error}
        />

        <AnimatePresence mode="wait">
          {isValid && (
            <IconWrapper
              as={motion.div}
              initial={reducedMotion ? {} : { scale: 0, opacity: 0 }}
              animate={reducedMotion ? {} : { scale: 1, opacity: 1 }}
              exit={reducedMotion ? {} : { scale: 0, opacity: 0 }}
              transition={reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 400, damping: 20 }}
            >
              <SuccessIcon>
                <CheckCircle />
              </SuccessIcon>
            </IconWrapper>
          )}
          {error && (
            <IconWrapper
              as={motion.div}
              initial={reducedMotion ? {} : { scale: 0, opacity: 0 }}
              animate={reducedMotion ? {} : { scale: 1, opacity: 1 }}
              exit={reducedMotion ? {} : { scale: 0, opacity: 0 }}
              transition={reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 400, damping: 20 }}
            >
              <ErrorIcon>
                <ErrorOutline />
              </ErrorIcon>
            </IconWrapper>
          )}
        </AnimatePresence>

        {maxNumOfCharacters && (
          <Counter $isMaxReached={isMaxReached || false}>
            {safeValue.length} / {maxNumOfCharacters}
          </Counter>
        )}
      </InputWrapper>

      <AnimatePresence mode="wait">
        {error && (
          <ErrorText
            id={errorId}
            role="alert"
            aria-live="polite"
            as={motion.div}
            initial={reducedMotion ? {} : { opacity: 0, y: -10 }}
            animate={reducedMotion ? {} : { opacity: 1, y: 0 }}
            exit={reducedMotion ? {} : { opacity: 0, y: -10 }}
            transition={reducedMotion ? { duration: 0 } : { duration: 0.2 }}
          >
            {error}
          </ErrorText>
        )}
      </AnimatePresence>
    </>
  );
};

export default KLTextInput;
